import { useEffect, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/**
 * Areas of law live in settings/areasOfLaw as { version, items: [{id, label, isActive}] }.
 * File subtypes live separately in settings/general.fileSubtypes as a bare array of
 * {id, label} — they were seeded onto a different document than areas of law, so this
 * page has to read/write both docs rather than treating them as one blob. See
 * functions/index.js:adminSeedDefaultSettings and functions/scripts/seed.js for the
 * canonical shapes.
 *
 * This page used to also carry the matter-completion-email and final-distribution-email
 * settings, purely because they happened to live in the same settings/general document —
 * an accident of storage, not a reason to group them under "Areas of law" in the nav. The
 * completion-email cc list moved to Admin → Workflow (it's about the workflow's completion
 * step); the final-distribution list moved to Admin → Numbering (it fires right after
 * numbers are assigned there). Both still write to settings/general via
 * preserveGeneralSettings — see lib/generalSettings.js's doc comment for why a partial
 * write there would silently clobber every other admin page's fields.
 */
export default function AdminAreasOfLaw() {
  const { data: areasOfLawDoc, loading: loadingAreas } = useDoc('settings/areasOfLaw');
  const { data: generalDoc, loading: loadingGeneral } = useDoc('settings/general');
  const { run, busy, error, saved } = useCallable();
  const [items, setItems] = useState(null);
  const [fileSubtypes, setFileSubtypes] = useState(null);

  // Initialize from whatever the doc snapshot resolved to, including a missing doc
  // (areasOfLawDoc/generalDoc === null). Gating on the truthiness of the doc itself
  // (the old `if (areasOfLawDoc && ...)` / `if (generalDoc && ...)` checks) meant that
  // whenever settings/general did not exist yet in production, fileSubtypes would stay
  // permanently null, and since `loading` below OR's in `fileSubtypes === null`, the
  // whole page got stuck on "Loading…" forever — this is the concrete "Areas of law
  // still not working" bug. Gate on loadingAreas/loadingGeneral (which useDoc always
  // resolves to false once the snapshot comes back, missing or not) instead.
  useEffect(() => {
    if (!loadingAreas && items === null) setItems(structuredClone(areasOfLawDoc?.items || []));
  }, [loadingAreas, areasOfLawDoc, items]);
  useEffect(() => {
    if (!loadingGeneral && fileSubtypes === null) {
      setFileSubtypes(structuredClone(generalDoc?.fileSubtypes || []));
    }
  }, [loadingGeneral, generalDoc, fileSubtypes]);

  const loading = loadingAreas || loadingGeneral || items === null || fileSubtypes === null;
  if (loading) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const save = async () => {
    const first = await run('adminUpdateSettings', {
      docId: 'areasOfLaw',
      value: { version: (areasOfLawDoc?.version || 0) + 1, items },
    });
    if (!first) return; // stop on failure so the error stays visible and general isn't touched
    // settings/general holds other fields (firmName, appName, supportContact) that
    // adminUpdateSettings overwrites wholesale (merge: false), so we must send the
    // full document back, not just fileSubtypes.
    await run('adminUpdateSettings', { docId: 'general', value: { ...generalDoc, fileSubtypes } });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      <ListEditor
        title="Areas of law"
        hint="Unchecking an area hides it from new intake forms but leaves it on existing matters."
        items={items}
        onChange={setItems}
      />
      <ListEditor
        title="File subtypes"
        hint="The “Files to be opened” checkboxes."
        items={fileSubtypes}
        onChange={setFileSubtypes}
        hideActiveToggle
      />
    </div>
  );
}

function ListEditor({ title, hint, items, onChange, hideActiveToggle }) {
  const [draft, setDraft] = useState('');
  const rows = Array.isArray(items) ? items : [];

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>
      <ul className="mt-3 grid gap-1 sm:grid-cols-2">
        {rows.map((item, index) => (
          <li key={item.id} className="flex items-center gap-2">
            {!hideActiveToggle && (
              <input
                type="checkbox"
                checked={item.isActive !== false}
                onChange={(e) =>
                  onChange(
                    rows.map((it, i) => (i === index ? { ...it, isActive: e.target.checked } : it))
                  )
                }
              />
            )}
            <input
              className="flex-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
              value={item.label}
              onChange={(e) =>
                onChange(rows.map((it, i) => (i === index ? { ...it, label: e.target.value } : it)))
              }
            />
            <code className="w-40 shrink-0 text-xs text-slate-400 dark:text-slate-500">{item.id}</code>
            <button
              type="button"
              className="text-xs text-red-600 dark:text-red-400 hover:underline"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2">
        <input
          className="flex-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          placeholder={`Add a new ${title.toLowerCase().replace(/s$/, '')}…`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button
          type="button"
          disabled={!draft.trim()}
          onClick={() => {
            onChange([...rows, { id: slug(draft), label: draft.trim(), isActive: true }]);
            setDraft('');
          }}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-sm disabled:opacity-40"
        >
          Add
        </button>
      </div>
    </section>
  );
}
